import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { CalendarDays, MessageCircle, ChevronRight, Calendar, Send } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";

function StatusBadge({ status }) {
  const map = {
    accepting: { label: "希望受付中", cls: "bg-blue-50 text-blue-700 border-blue-200" },
    adjusting: { label: "調整中", cls: "bg-yellow-50 text-yellow-700 border-yellow-200" },
    confirmed: { label: "確定済み", cls: "bg-green-50 text-green-700 border-green-200" },
    published: { label: "公開済み", cls: "bg-green-50 text-green-700 border-green-200" },
    closed: { label: "終了", cls: "bg-slate-100 text-slate-600 border-slate-200" },
  };
  const m = map[status] || { label: status, cls: "bg-slate-100 text-slate-600 border-slate-200" };
  return (
    <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${m.cls}`}>
      {m.label}
    </span>
  );
}

export default function StaffHome() {
  const { user } = useAuth();
  const [periods, setPeriods] = useState([]);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [unreadNotifs, setUnreadNotifs] = useState(0);
  const [myRequests, setMyRequests] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/shift-periods").then((r) => setPeriods(r.data)),
      api.get("/messages/unread-count").then((r) => setUnreadMessages(r.data.count)),
      api.get("/notifications/unread-count").then((r) => setUnreadNotifs(r.data.count)),
      api.get("/shift-requests").then((r) => setMyRequests(r.data)),
    ]).catch(() => {});
  }, []);

  const acceptingPeriod = periods.find((p) => p.status === "accepting");
  const publishedPeriod = periods.find((p) => p.status === "published");
  const mySubmittedCount = acceptingPeriod
    ? myRequests.filter((r) => r.shift_period_id === acceptingPeriod.id).length
    : 0;

  return (
    <div className="p-4 space-y-4 max-w-md mx-auto" data-testid="staff-home">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-slate-900">
          こんにちは、{user?.display_name || user?.name?.split(" ")[0] || user?.name} さん
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">今日のシフト状況を確認しましょう</p>
      </div>

      {/* Next month submission card */}
      {acceptingPeriod ? (
        <div
          data-testid="staff-home-accepting-card"
          className="bg-white border border-slate-200 rounded-lg shadow-sm p-5"
        >
          <div className="flex items-start justify-between mb-3">
            <div>
              <p className="text-xs font-medium tracking-wider text-slate-500 uppercase">
                希望提出
              </p>
              <h2 className="text-lg font-bold text-slate-900 mt-0.5">
                {acceptingPeriod.year}年{acceptingPeriod.month}月分シフト
              </h2>
            </div>
            <StatusBadge status={acceptingPeriod.status} />
          </div>
          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-500">提出期限</span>
              <span className="font-semibold text-slate-900">
                {acceptingPeriod.submission_deadline}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">提出済み日数</span>
              <span className="font-semibold text-slate-900">{mySubmittedCount}日</span>
            </div>
          </div>
          <Link to="/staff/request">
            <Button
              data-testid="staff-home-submit-button"
              className="w-full mt-4 bg-blue-600 hover:bg-blue-700 h-12 text-base font-semibold"
            >
              <Send className="w-4 h-4 mr-2" />
              希望を提出する
            </Button>
          </Link>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-5 text-sm text-slate-500">
          現在、希望受付中の対象月はありません
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <Link
          to="/staff/messages"
          data-testid="staff-home-messages-card"
          className="bg-white border border-slate-200 rounded-lg shadow-sm p-4 flex flex-col justify-between min-h-[100px]"
        >
          <div className="flex items-center justify-between">
            <MessageCircle className="w-5 h-5 text-blue-600" />
            {unreadMessages > 0 && (
              <span className="bg-red-600 text-white text-xs font-bold rounded-full px-2 py-0.5">
                {unreadMessages}
              </span>
            )}
          </div>
          <div>
            <p className="text-xs text-slate-500">未読</p>
            <p className="text-lg font-bold text-slate-900">{unreadMessages} 件</p>
            <p className="text-xs text-slate-500 mt-1">メッセージを見る ›</p>
          </div>
        </Link>
        <Link
          to="/staff/my-shifts"
          data-testid="staff-home-shifts-card"
          className="bg-white border border-slate-200 rounded-lg shadow-sm p-4 flex flex-col justify-between min-h-[100px]"
        >
          <div className="flex items-center justify-between">
            <CalendarDays className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <p className="text-xs text-slate-500">確定シフト</p>
            <p className="text-sm font-bold text-slate-900">
              {publishedPeriod
                ? `${publishedPeriod.year}年${publishedPeriod.month}月分 公開中`
                : "未公開"}
            </p>
            <p className="text-xs text-slate-500 mt-1">自分のシフト ›</p>
          </div>
        </Link>
      </div>

      <Link
        to="/staff/setup"
        className="block bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800 flex items-center gap-3"
        data-testid="staff-home-setup-link"
      >
        <ChevronRight className="w-5 h-5 shrink-0 rotate-180" />
        <span className="flex-1">表示名・デフォルト時間を変更する</span>
        <ChevronRight className="w-4 h-4" />
      </Link>

      <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-4">
        <h3 className="text-sm font-semibold text-slate-900 mb-2">これまでの対象月</h3>
        <ul className="space-y-1">
          {periods.slice(0, 5).map((p) => (
            <li
              key={p.id}
              className="flex items-center justify-between py-1.5 text-sm"
              data-testid={`staff-period-${p.id}`}
            >
              <span className="text-slate-700">
                {p.year}年{p.month}月分
              </span>
              <StatusBadge status={p.status} />
            </li>
          ))}
          {periods.length === 0 && (
            <li className="text-sm text-slate-400">対象月がまだ作成されていません</li>
          )}
        </ul>
      </div>
    </div>
  );
}
